<template>
  <div style="max-width: 450px; margin: 0 auto;">
    <h4 class="text-center">Visits by Operating System</h4>
    <Doughnut v-if="chartData" :data="chartData" :options="chartOptions" />
    <div v-else>Loading...</div>
  </div>
</template>


<script setup>
import { ref, onMounted } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from 'axios';

Chart.register(ArcElement, Tooltip, Legend);

const chartData = ref(null);
/**
 * Configuration options for a chart.
 *
 * @typedef {Object} chartOptions
 * @property {boolean} responsive - Indicates if the chart should resize responsively with the container.
 * @property {Object} plugins - Plugin-related settings for the chart.
 * @property {Object} plugins.legend - Configuration for the legend display.
 * @property {boolean} plugins.legend.display - Determines if the legend should be displayed.
 * @property {string} plugins.legend.position - Position of the legend. Acceptable values may vary depending on the chart library.
 * @property {Object} plugins.title - Configuration for the chart title display.
 * @property {boolean} plugins.title.display - Determines if the title should be displayed.
 * @property {string} plugins.title.text - The text to display as the chart title.
 */
const chartOptions = {
  responsive: true,
  plugins: {
    legend: { display: true, position: 'bottom' },
    title: { display: true, text: 'Visits by Operating System' }
  }
};

/**
 * An array of color codes in hexadecimal format.
 * The array contains a selection of colors, primarily representing
 * shades of blue, purple, pink, and red. Additional colors such as
 * orange, yellow, green, and light blue are commented out.
 */
const colors = [
  '#0d6efd',
  '#6610f2',
  '#6f42c1',
  '#d63384',
  '#dc3545'
];

onMounted(/**
 * Fetches user-specific link visit data from an API and processes it for chart rendering.
 *
 * The function retrieves the API URL and user token from environment variables and session storage.
 * It sends a GET request to the API endpoint, including the user ID in the URL and authorization
 * token in the headers. The response data is then processed to count the frequency of visited links
 * grouped by a specific attribute (`so`).
 *
 * - If the response contains valid data with an array of visited links, it calculates the counts and
 *   creates a chart configuration.
 * - If the response is invalid or an error occurs, an empty chart configuration is set.
 *
 * Data used for chart rendering includes `labels` (unique attribute group names) and `data` (visit counts).
 */
async () => {
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');

  try {
    const id = sessionStorage.getItem('id');
    const res = await axios.get(`${apiUrl}/linksvisited/id_user/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res && Array.isArray(res.data.linksVisited)) {
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const so = item?.so || 'Unknown';
        counts[so] = (counts[so] || 0) + 1;
      });
      const labels = Object.keys(counts);
      chartData.value = {
        labels,
        datasets: [{
          label: 'Visits',
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          data: Object.values(counts)
        }]
      };
    } else {
      chartData.value = {
        labels: [],
        datasets: [{ label: 'Visits', data: [] }]
      };
    }
  } catch (e) {
    chartData.value = {
      labels: [],
      datasets: [{ label: 'Visits', data: [] }]
    };
  }
});
</script>
